import React, { useEffect, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import "../styles/ConversationalAnalysis.scss";
import MicIcon from "@mui/icons-material/Mic";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import SendIcon from "@mui/icons-material/Send";
import {
  getConversationAnalysis,
  getContentConversation,
} from "../helper/apicalls/conversationalAnalysis";

const LiveVisualizer = ({ stream }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const analyserRef = useRef(null);
  const contextRef = useRef(null);

  useEffect(() => {
    if (!stream || !canvasRef.current) return;

    const AudioContext = window.AudioContext || window.webkitAudioContext;
    const audioContext = new AudioContext();
    contextRef.current = audioContext;
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 64;
    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);
    analyserRef.current = analyser;

    const canvas = canvasRef.current;
    const canvasCtx = canvas.getContext("2d");
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      if (!canvas) return;

      const width = canvas.width;
      const height = canvas.height;

      animationRef.current = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);

      canvasCtx.clearRect(0, 0, width, height);

      const barWidth = 4;
      const gap = 3;
      const totalWidth = bufferLength * (barWidth + gap);
      let startX = (width - totalWidth) / 2;
      if (startX < 0) startX = 0;

      canvasCtx.fillStyle = "#ff5252";

      for (let i = 0; i < bufferLength; i++) {
        const value = dataArray[i];
        const percent = value / 255;
        const barHeight = Math.max(4, height * percent * 0.9);

        const x = startX + i * (barWidth + gap);
        const y = (height - barHeight) / 2;

        canvasCtx.beginPath();
        canvasCtx.roundRect(x, y, barWidth, barHeight, 2);
        canvasCtx.fill();
      }
    };

    draw();

    return () => {
      cancelAnimationFrame(animationRef.current);
      if (contextRef.current && contextRef.current.state !== "closed") {
        contextRef.current.close();
      }
    };
  }, [stream]);

  return (
    <canvas
      ref={canvasRef}
      width={300}
      height={48}
      style={{ width: "100%", height: "100%" }}
    />
  );
};

const WaveformPlayer = ({
  audioUrl,
  blob,
  isAssistant,
  duration,
  onPlayStateChange,
}) => {
  const containerRef = useRef(null);
  const waveSurferRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(duration || 0);

  const hasAudioSource = !!(audioUrl || blob);

  useEffect(() => {
    if (!containerRef.current || !hasAudioSource) return;

    const ws = WaveSurfer.create({
      container: containerRef.current,
      waveColor: "#b0bec5",
      progressColor: "#54656f",
      cursorColor: "transparent",
      barWidth: 3,
      barGap: 3,
      barRadius: 3,
      height: 30,
      normalize: true,
      url: audioUrl,
      interact: true,
    });

    if (blob && !audioUrl) {
      ws.loadBlob(blob);
    }

    ws.on("ready", () => {
      const dur = ws.getDuration();
      if (dur > 0) setTotalDuration(dur);
    });

    ws.on("audioprocess", () => {
      setCurrentTime(ws.getCurrentTime());
    });

    ws.on("finish", () => {
      setIsPlaying(false);
      setCurrentTime(0);
      if (onPlayStateChange) onPlayStateChange(false);
    });

    ws.on("play", () => {
      setIsPlaying(true);
      if (onPlayStateChange) onPlayStateChange(true);
    });

    ws.on("pause", () => {
      setIsPlaying(false);
      if (onPlayStateChange) onPlayStateChange(false);
    });

    waveSurferRef.current = ws;

    return () => {
      ws.destroy();
    };
  }, [audioUrl, blob, isAssistant, hasAudioSource, onPlayStateChange]);

  const togglePlay = (e) => {
    e.stopPropagation();
    if (waveSurferRef.current) {
      waveSurferRef.current.playPause();
    }
  };

  const formatTime = (time) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (!hasAudioSource) return null;

  return (
    <div className="voice-message-player">
      <button className="play-btn-small" onClick={togglePlay}>
        {isPlaying ? (
          <PauseIcon fontSize="small" />
        ) : (
          <PlayArrowIcon fontSize="small" />
        )}
      </button>

      <div className="waveform-container" ref={containerRef} />

      <div className="voice-info-inline">
        <span>
          {formatTime(currentTime)} / {formatTime(totalDuration)}
        </span>
      </div>
    </div>
  );
};

const StaticWaveform = ({ progress, timestamp, isPlaying }) => {
  const canvasRef = useRef(null);
  const [peaks] = useState(() =>
    Array.from({ length: 45 }, () => Math.random() * 0.6 + 0.2),
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const width = canvas.width;
    const height = canvas.height;
    ctx.clearRect(0, 0, width, height);

    const barWidth = 3;
    const gap = 3;
    const totalBarWidth = barWidth + gap;
    const totalWidth = peaks.length * totalBarWidth;
    const startX = width - totalWidth > 0 ? (width - totalWidth) / 2 : 0;

    const playedColor = "#54656f";
    const unplayedColor = "#b0bec5";

    peaks.forEach((peak, i) => {
      const x = startX + i * totalBarWidth;
      const barHeight = height * peak;
      const y = (height - barHeight) / 2;

      const barProgress = i / peaks.length;
      ctx.fillStyle = barProgress < progress ? playedColor : unplayedColor;

      ctx.beginPath();
      ctx.roundRect(x, y, barWidth, barHeight, 2);
      ctx.fill();
    });
  }, [peaks, progress]);

  return (
    <div className="voice-message-player">
      <button className="play-btn-small">
        {isPlaying ? (
          <PauseIcon fontSize="small" />
        ) : (
          <PlayArrowIcon fontSize="small" />
        )}
      </button>

      <div
        className="waveform-container"
        style={{ display: "flex", alignItems: "center" }}
      >
        <canvas
          ref={canvasRef}
          width={280}
          height={30}
          style={{ width: "100%", height: "100%" }}
        />
      </div>

      <div className="voice-info-inline">
        <span>{timestamp}</span>
      </div>
    </div>
  );
};

const ConversationalAnalysis = ({
  question = "",
  onNext,
  contentConvo = false,
  apiQueryParams,
}) => {
  const [messages, setMessages] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [playingMessageId, setPlayingMessageId] = useState(null);

  const [ttsProgress, setTTSProgress] = useState(0);
  const ttsIntervalRef = useRef(null);

  const [recorderStream, setRecorderStream] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);

  const [inputText, setInputText] = useState("");
  const [error, setError] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const recordingStartTimeRef = useRef(null);
  const questionAddedRef = useRef(null);
  const endRef = useRef(null);

  const supportsSpeech =
    typeof window !== "undefined" && "speechSynthesis" in window;

  const getSamanthaVoice = () => {
    const voices = window.speechSynthesis.getVoices();
    return (
      voices.find((voice) => voice.name === "Samantha") ||
      voices.find((voice) => voice.name.includes("Female")) ||
      voices.find((voice) => voice.lang === "en-US") ||
      null
    );
  };

  const playTTS = React.useCallback(
    (message) => {
      if (!supportsSpeech || !message.text) return;

      if (playingMessageId === message.id && isSpeaking) {
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
        setPlayingMessageId(null);
        if (ttsIntervalRef.current) clearInterval(ttsIntervalRef.current);
        return;
      }

      if (playingMessageId) {
        window.speechSynthesis.cancel();
        if (ttsIntervalRef.current) clearInterval(ttsIntervalRef.current);
      }

      setTTSProgress(0);

      const utterance = new SpeechSynthesisUtterance(message.text);
      const voice = getSamanthaVoice();
      if (voice) utterance.voice = voice;

      const estimatedDurationMs = Math.max(
        1000,
        (message.text.length / 15) * 1000,
      );
      const startTime = Date.now();

      utterance.onstart = () => {
        setIsSpeaking(true);
        setPlayingMessageId(message.id);

        if (ttsIntervalRef.current) clearInterval(ttsIntervalRef.current);

        ttsIntervalRef.current = setInterval(() => {
          const elapsed = Date.now() - startTime;
          const prog = Math.min(elapsed / estimatedDurationMs, 0.95);
          setTTSProgress(prog);
        }, 50);
      };

      utterance.onend = () => {
        if (ttsIntervalRef.current) clearInterval(ttsIntervalRef.current);
        setTTSProgress(1.0);
        setIsSpeaking(false);
        setPlayingMessageId(null);

        setTimeout(() => {
          if (playingMessageId === message.id) return;
          setTTSProgress(0);
        }, 1500);
      };

      utterance.onerror = () => {
        if (ttsIntervalRef.current) clearInterval(ttsIntervalRef.current);
        setIsSpeaking(false);
        setPlayingMessageId(null);
        setTTSProgress(0);
      };

      window.speechSynthesis.speak(utterance);
    },
    [
      supportsSpeech,
      playingMessageId,
      isSpeaking,
      getSamanthaVoice,
      setIsSpeaking,
      setPlayingMessageId,
      setTTSProgress,
    ],
  );

  useEffect(() => {
    if (question && questionAddedRef.current !== question) {
      questionAddedRef.current = question;
      setIsRecording(false);
      setIsProcessing(false);
      setIsSpeaking(false);
      setPlayingMessageId(null);
      setTTSProgress(0);
      setError(null);
      setRecordingDuration(0);

      if (supportsSpeech) {
        window.speechSynthesis.cancel();
      }

      const questionMessage = {
        id: "question-" + Date.now(),
        role: "assistant",
        text: question,
        isAudio: !contentConvo,
        isQuestion: true,
        timestamp: new Date().toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };
      setMessages([questionMessage]);

      if (!contentConvo) {
        setTimeout(() => {
          if (supportsSpeech) {
            playTTS(questionMessage);
          }
        }, 800);
      }
    }
  }, [question, supportsSpeech, contentConvo]);

  useEffect(() => {
    return () => {
      if (supportsSpeech) {
        window.speechSynthesis.cancel();
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (ttsIntervalRef.current) {
        clearInterval(ttsIntervalRef.current);
      }
      if (recorderStream) {
        recorderStream.getTracks().forEach((t) => t.stop());
      }
    };
  }, [supportsSpeech, recorderStream]);

  useEffect(() => {
    let interval = null;
    if (isRecording) {
      interval = setInterval(() => {
        if (recordingStartTimeRef.current) {
          const elapsed = Math.floor(
            (Date.now() - recordingStartTimeRef.current) / 1000,
          );
          setRecordingDuration(elapsed);
        }
      }, 1000);
    } else if (!isRecording && !isProcessing) {
      setRecordingDuration(0);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRecording, isProcessing]);

  useEffect(() => {
    if (!endRef.current) return;

    const scrollContainer =
      endRef.current.closest(".chat-window") ||
      endRef.current.closest(".conv-left") ||
      endRef.current.parentElement;

    if (scrollContainer && typeof scrollContainer.scrollTo === "function") {
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: "smooth",
      });
    } else {
      endRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [messages]);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const startRecording = async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setRecorderStream(stream);

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      recordingStartTimeRef.current = Date.now();
      setRecordingDuration(0);
      setIsRecording(true);

      mediaRecorderRef.current.transcribedText = "";
      mediaRecorderRef.current.recognition = null;

      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = "en-US";

        recognition.onresult = (event) => {
          let finalTranscript = "";
          for (let i = event.resultIndex; i < event.results.length; i++) {
            if (event.results[i].isFinal) {
              finalTranscript += event.results[i][0].transcript + " ";
            }
          }
          if (finalTranscript) {
            mediaRecorderRef.current.transcribedText += finalTranscript;
          }
        };

        mediaRecorderRef.current.recognition = recognition;
        try {
          recognition.start();
        } catch (e) {}
      }

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        clearInterval(recordingTimerRef.current);
        const wasCancelled = mediaRecorderRef.current?.cancelled;

        if (mediaRecorderRef.current.recognition) {
          try {
            mediaRecorderRef.current.recognition.stop();
          } catch (e) {}
        }

        if (wasCancelled) {
          setRecorderStream(null);
          return;
        }

        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });
        const transcribedText =
          mediaRecorderRef.current.transcribedText?.trim() || "";

        const finalDuration = Math.floor(
          (Date.now() - recordingStartTimeRef.current) / 1000,
        );

        await sendAudioToAPI(audioBlob, finalDuration, transcribedText);

        if (stream) {
          stream.getTracks().forEach((t) => t.stop());
        }
        setRecorderStream(null);
        setRecordingDuration(0);
      };

      mediaRecorder.start();
    } catch (err) {
      console.error(err);
      setError("Failed to access microphone.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.cancelled = true;
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setRecordingDuration(0);
      if (recorderStream) {
        recorderStream.getTracks().forEach((t) => t.stop());
        setRecorderStream(null);
      }
    }
  };

  const handleAPIResponse = (response, { isFromText = false } = {}) => {
    const responseData = response.data;
    setMessages((prev) => prev.filter((m) => !m.isThinking));

    let responseText = "No response received";
    let mark = null;

    if (responseData?.content) {
      try {
        const parsedContent = JSON.parse(responseData.content);
        if (parsedContent.result) {
          responseText = parsedContent.result;
        } else {
          responseText = responseData.content;
        }
      } catch (e) {
        responseText = responseData.content;
      }
    } else if (responseData?.result) {
      responseText = responseData.result;
      mark = responseData.mark;
    } else if (responseData?.questions?.[0]?.result) {
      responseText = responseData.questions[0].result;
      mark = responseData.questions[0].mark;
    } else if (responseData?.structured?.questions?.[0]?.result) {
      responseText = responseData.structured.questions[0].result;
      mark = responseData.structured.questions[0].mark;
    } else if (responseData?.result) {
      responseText = responseData.result;
      mark = responseData.mark;
    } else if (responseData?.text || responseData?.message) {
      responseText = responseData.text || responseData.message;
    }

    const assistantMessage = {
      id: Date.now() + 1,
      role: "assistant",
      text: responseText,
      isAudio: !isFromText && !contentConvo,
      isResponse: true,
      mark: mark,
      timestamp: new Date().toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
    setMessages((prev) => [...prev, assistantMessage]);

    if (!isFromText && !contentConvo) {
      setTimeout(() => {
        if (supportsSpeech) {
          playTTS(assistantMessage);
        }
      }, 500);
    }

    if ((mark === 1 || mark === "1") && onNext) {
      setTimeout(onNext, 5000);
    }
  };

  const sendAudioToAPI = async (audioBlob, duration, transcribedText) => {
    try {
      setIsProcessing(true);
      setError(null);

      const audioUrl = URL.createObjectURL(audioBlob);

      const userMessage = {
        id: Date.now(),
        role: "user",
        text: transcribedText || "[Audio message]",
        isAudio: true,
        audioUrl: audioUrl,
        blob: audioBlob,
        duration: duration,
        timestamp: new Date().toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };

      setMessages((prev) => [...prev, userMessage]);

      const thinkingMessage = {
        id: "thinking-" + Date.now(),
        role: "assistant",
        isThinking: true,
        timestamp: new Date().toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };
      setMessages((prev) => [...prev, thinkingMessage]);

      const mp3Blob = new Blob([audioBlob], { type: "audio/mp3" });
      const formData = new FormData();
      formData.append("audio", mp3Blob, "audio.mp3");

      let response;
      if (contentConvo) {
        response = await getContentConversation(formData, apiQueryParams);
      } else {
        if (question) {
          formData.append("user_data", JSON.stringify({ question }));
        }
        response = await getConversationAnalysis(formData);
      }

      handleAPIResponse(response);
    } catch (err) {
      console.error(err);
      setMessages((prev) => prev.filter((m) => !m.isThinking));
      setError("Failed to process audio.");
    } finally {
      setIsProcessing(false);
    }
  };

  const sendTextToAPI = async () => {
    if (!inputText.trim()) return;

    const textToSend = inputText.trim();
    setInputText("");

    try {
      setIsProcessing(true);
      setError(null);

      const userMessage = {
        id: Date.now(),
        role: "user",
        text: textToSend,
        isAudio: false,
        timestamp: new Date().toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };

      setMessages((prev) => [...prev, userMessage]);

      const thinkingMessage = {
        id: "thinking-" + Date.now(),
        role: "assistant",
        isThinking: true,
        timestamp: new Date().toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };
      setMessages((prev) => [...prev, thinkingMessage]);

      const formData = new FormData();
      const userData = { question: textToSend };
      formData.append("user_data", JSON.stringify(userData));

      const response = await getContentConversation(formData, apiQueryParams);
      handleAPIResponse(response, { isFromText: true });
    } catch (err) {
      console.error(err);
      setMessages((prev) => prev.filter((m) => !m.isThinking));
      setError("Failed to process text.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="conv-container">
      <div className="conv-grid">
        <section className="conv-left custom-scrollbar">
          <div className="chat-window custom-scrollbar" role="list">
            {messages.map((m) => (
              <div key={m.id} className={`msg ${m.role}`}>
                <div className="voice-message-bubble">
                  {m.isThinking ? (
                    <div className="thinking-text">Thinking...</div>
                  ) : contentConvo && m.role === "assistant" ? (
                    <div className="voice-message-player assistant">
                      {supportsSpeech && m.text && (
                        <button
                          className="play-btn-small"
                          onClick={(e) => {
                            e.stopPropagation();
                            playTTS(m);
                          }}
                        >
                          {playingMessageId === m.id && isSpeaking ? (
                            <PauseIcon />
                          ) : (
                            <PlayArrowIcon />
                          )}
                        </button>
                      )}
                      <div className="text-message">{m.text}</div>
                    </div>
                  ) : m.role === "user" ? (
                    m.isAudio ? (
                      <WaveformPlayer
                        audioUrl={m.audioUrl}
                        blob={m.blob}
                        duration={m.duration}
                        isAssistant={false}
                      />
                    ) : (
                      <div className="text-message">{m.text}</div>
                    )
                  ) : m.isAudio ? (
                    m.audioUrl || m.blob ? (
                      <WaveformPlayer
                        audioUrl={m.audioUrl}
                        blob={m.blob}
                        duration={m.duration}
                        isAssistant={true}
                      />
                    ) : (
                      <div
                        className="voice-message-simulated"
                        onClick={() => playTTS(m)}
                        style={{ cursor: "pointer" }}
                      >
                        <StaticWaveform
                          progress={playingMessageId === m.id ? ttsProgress : 0}
                          timestamp={m.timestamp}
                          isPlaying={playingMessageId === m.id && isSpeaking}
                        />
                      </div>
                    )
                  ) : (
                    <div className="text-message">{m.text}</div>
                  )}
                </div>
              </div>
            ))}
            {messages.length === 0 && (
              <div className="conv-empty">Waiting for question...</div>
            )}
            <div ref={endRef} />
          </div>

          {error && <div className="error-message">{error}</div>}

          {isRecording && (
            <div className="recording-ui">
              <button className="cancel-btn" onClick={cancelRecording}>
                <DeleteIcon />
              </button>
              <div className="recording-waveform">
                <LiveVisualizer stream={recorderStream} />
              </div>
              <div className="recording-duration">
                {formatDuration(recordingDuration)}
              </div>
              <button className="send-btn" onClick={stopRecording}>
                <SendIcon />
              </button>
            </div>
          )}

          {!isRecording && (
            <div className="mic-controls">
              {contentConvo ? (
                <div className="wa-input-container">
                  <input
                    type="text"
                    className="wa-text-input"
                    placeholder="Type a message"
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        sendTextToAPI();
                      }
                    }}
                    disabled={isProcessing}
                  />
                  {inputText.trim() ? (
                    <button
                      className="wa-send-btn"
                      onClick={sendTextToAPI}
                      disabled={isProcessing}
                    >
                      <SendIcon />
                    </button>
                  ) : (
                    <button
                      className="wa-mic-btn"
                      onClick={startRecording}
                      disabled={isProcessing}
                    >
                      <MicIcon />
                    </button>
                  )}
                </div>
              ) : (
                <button
                  className={`mic-button ${isProcessing ? "disabled" : ""}`}
                  onClick={startRecording}
                  disabled={isProcessing}
                >
                  <MicIcon className="mic-icon" />
                </button>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ConversationalAnalysis;
